import { Mat4, Vec3 } from 'playcanvas';

import { Splat } from './splat';
import { State } from './splat-state';

export type VolugraphAutoCenterDensityCfg = {
    enabled?: boolean;
    voxelResolution?: number;
    paddingFactor?: number;
    maxSamples?: number;
    /** 0–1 blend of focal Y toward splat bound center (reduces ground-plane bias). */
    liftTowardBoundCenter?: number;
    /** Multiply computed framing radius (e.g. 1.35 for commerce). */
    zoomOutFactor?: number;
};

export type DensityFocusResult = {
    focalPoint: Vec3;
    radius: number;
    /** Orbit azimuth (deg) when smart angle is confident; else caller uses scene defaults. */
    azimDeg?: number;
    /** Orbit elevation (deg) when smart angle is confident; else caller uses scene defaults. */
    elevDeg?: number;
};

const DEFAULT_VOXEL_RES = 56;
const DEFAULT_PADDING = 1.2;
const DEFAULT_MAX_SAMPLES = 1_200_000;
/** Voxel radius around density peak for centroid / extent estimation */
const NEIGHBOR_VOXELS = 2;
/** Pleasant downward orbit for subject framing (world Y-up). */
const DEFAULT_SMART_ELEV_DEG = 24;
/** Keep home view meaningfully above the subject by default. */
const DEFAULT_MIN_ABOVE_GROUND_ELEV_DEG = 12;
/** Require measurable horizontal elongation before picking a smart azimuth. */
const EIGEN_RATIO_MIN = 1.15;

const worldMat = new Mat4();
const localDir = new Vec3();
const worldDir = new Vec3();

const opacityWeight = (opacity: number): number => {
    if (!Number.isFinite(opacity)) return 1;
    if (opacity <= -20) return 0.001;
    if (opacity >= 20) return 1;
    return 1 / (1 + Math.exp(-opacity));
};

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

const voxelIndex = (ix: number, iy: number, iz: number, r: number) => ix + iy * r + iz * r * r;

/**
 * Derive orbit azimuth from the subject's minor horizontal axis (broad-side view).
 * Returns empty when the cluster is too symmetric to pick a stable angle.
 */
const computeSmartOrbitAngles = (
    splat: Splat,
    sxx: number,
    sxz: number,
    szz: number,
    wSum: number
): { azimDeg?: number; elevDeg?: number } => {
    if (wSum < 1e-9) return {};

    const nxx = sxx / wSum;
    const nxz = sxz / wSum;
    const nzz = szz / wSum;

    const trace = nxx + nzz;
    const det = nxx * nzz - nxz * nxz;
    const disc = Math.sqrt(Math.max(0, trace * trace * 0.25 - det));
    const lambdaMajor = trace * 0.5 + disc;
    const lambdaMinor = trace * 0.5 - disc;

    if (lambdaMajor < 1e-12 || lambdaMinor < 1e-12) return {};
    if (lambdaMajor / Math.max(lambdaMinor, 1e-12) < EIGEN_RATIO_MIN) return {};

    let evX = nxz;
    let evZ = lambdaMajor - nxx;
    let evLen = Math.hypot(evX, evZ);
    if (evLen < 1e-9) {
        evX = lambdaMajor - nzz;
        evZ = nxz;
        evLen = Math.hypot(evX, evZ);
    }
    if (evLen < 1e-9) return {};

    const majorX = evX / evLen;
    const majorZ = evZ / evLen;
    // Camera on minor axis → broad side of subject fills the frame.
    const minorX = -majorZ;
    const minorZ = majorX;

    localDir.set(minorX, 0, minorZ);
    worldMat.copy(splat.worldTransform);
    worldMat.transformVector(localDir, worldDir);

    let hx = worldDir.x;
    let hz = worldDir.z;
    const hLen = Math.hypot(hx, hz);
    if (hLen < 1e-9) return {};

    hx /= hLen;
    hz /= hLen;

    // Matches Camera.setPose: azim = atan2(forward.x, forward.z) for horizontal orbit.
    const azimDeg = Math.atan2(hx, hz) * (180 / Math.PI);

    return { azimDeg, elevDeg: DEFAULT_SMART_ELEV_DEG };
};

/**
 * Estimate orbit pivot + framing radius from the opacity-weighted spatial density peak.
 * Returns null on degenerate input so callers can fall back to scene.bound framing.
 */
const computeDensityFocus = (
    splat: Splat,
    config?: VolugraphAutoCenterDensityCfg
): DensityFocusResult | null => {
    const R = Math.max(16, Math.min(96, Math.floor(config?.voxelResolution ?? DEFAULT_VOXEL_RES)));
    const padding = config?.paddingFactor ?? DEFAULT_PADDING;
    const maxSamples = config?.maxSamples ?? DEFAULT_MAX_SAMPLES;

    const xs = splat.splatData.getProp('x') as Float32Array | undefined;
    const ys = splat.splatData.getProp('y') as Float32Array | undefined;
    const zs = splat.splatData.getProp('z') as Float32Array | undefined;
    if (!xs || !ys || !zs || xs.length === 0) return null;

    const opacity = splat.splatData.getProp('opacity') as Float32Array | undefined;
    const state = splat.splatData.getProp('state') as Uint8Array | undefined;
    const n = xs.length;
    const stride = n > maxSamples ? Math.ceil(n / maxSamples) : 1;

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    let sampleCount = 0;

    for (let i = 0; i < n; i += stride) {
        if (state && (state[i] & State.deleted)) continue;
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        const w = opacity ? opacityWeight(opacity[i]) : 1;
        if (w < 0.01) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (z < minZ) minZ = z;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
        if (z > maxZ) maxZ = z;
        sampleCount++;
    }

    if (sampleCount < 10) return null;

    const extentX = maxX - minX;
    const extentY = maxY - minY;
    const extentZ = maxZ - minZ;
    const maxExtent = Math.max(extentX, extentY, extentZ, 1e-6);
    const voxelSize = maxExtent / R;

    const gridSize = R * R * R;
    const grid = new Float32Array(gridSize);

    for (let i = 0; i < n; i += stride) {
        if (state && (state[i] & State.deleted)) continue;
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        const w = opacity ? opacityWeight(opacity[i]) : 1;
        if (w < 0.01) continue;
        const ix = Math.min(R - 1, Math.max(0, Math.floor((x - minX) / voxelSize)));
        const iy = Math.min(R - 1, Math.max(0, Math.floor((y - minY) / voxelSize)));
        const iz = Math.min(R - 1, Math.max(0, Math.floor((z - minZ) / voxelSize)));
        grid[voxelIndex(ix, iy, iz, R)] += w;
    }

    let peakIdx = 0;
    let peakVal = -1;
    const smoothed = new Float32Array(gridSize);

    for (let iz = 0; iz < R; iz++) {
        for (let iy = 0; iy < R; iy++) {
            for (let ix = 0; ix < R; ix++) {
                let sum = 0;
                for (let dz = -1; dz <= 1; dz++) {
                    for (let dy = -1; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                            const nx = ix + dx;
                            const ny = iy + dy;
                            const nz = iz + dz;
                            if (nx < 0 || nx >= R || ny < 0 || ny >= R || nz < 0 || nz >= R) continue;
                            sum += grid[voxelIndex(nx, ny, nz, R)];
                        }
                    }
                }
                const idx = voxelIndex(ix, iy, iz, R);
                smoothed[idx] = sum;
                if (sum > peakVal) {
                    peakVal = sum;
                    peakIdx = idx;
                }
            }
        }
    }

    if (peakVal <= 0) return null;

    const peakIx = peakIdx % R;
    const peakIy = Math.floor(peakIdx / R) % R;
    const peakIz = Math.floor(peakIdx / (R * R));

    const peakCx = minX + (peakIx + 0.5) * voxelSize;
    const peakCy = minY + (peakIy + 0.5) * voxelSize;
    const peakCz = minZ + (peakIz + 0.5) * voxelSize;
    const clusterRadiusSq = (NEIGHBOR_VOXELS * voxelSize) ** 2;

    let cx = 0;
    let cy = 0;
    let cz = 0;
    let wSum = 0;
    let dist2Sum = 0;

    for (let i = 0; i < n; i += stride) {
        if (state && (state[i] & State.deleted)) continue;
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        const w = opacity ? opacityWeight(opacity[i]) : 1;
        if (w < 0.01) continue;
        const dx = x - peakCx;
        const dy = y - peakCy;
        const dz = z - peakCz;
        if (dx * dx + dy * dy + dz * dz > clusterRadiusSq) continue;
        cx += x * w;
        cy += y * w;
        cz += z * w;
        wSum += w;
    }

    if (wSum < 1e-9) return null;

    cx /= wSum;
    cy /= wSum;
    cz /= wSum;

    let sxx = 0;
    let sxz = 0;
    let szz = 0;

    for (let i = 0; i < n; i += stride) {
        if (state && (state[i] & State.deleted)) continue;
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        const w = opacity ? opacityWeight(opacity[i]) : 1;
        if (w < 0.01) continue;
        const dx = x - peakCx;
        const dy = y - peakCy;
        const dz = z - peakCz;
        if (dx * dx + dy * dy + dz * dz > clusterRadiusSq) continue;
        const ddx = x - cx;
        const ddy = y - cy;
        const ddz = z - cz;
        dist2Sum += w * (ddx * ddx + ddy * ddy + ddz * ddz);
        sxx += w * ddx * ddx;
        sxz += w * ddx * ddz;
        szz += w * ddz * ddz;
    }

    let radius = Math.sqrt(dist2Sum / wSum) * padding;
    if (!Number.isFinite(radius) || radius < maxExtent * 0.02) {
        radius = maxExtent * 0.15 * padding;
    }

    const focalPoint = new Vec3(cx, cy, cz);
    worldMat.copy(splat.worldTransform);
    worldMat.transformPoint(focalPoint, focalPoint);

    const scale = splat.entity.getLocalScale();
    const maxScale = Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z), 1e-6);
    radius *= maxScale;

    if (!Number.isFinite(focalPoint.x) || !Number.isFinite(focalPoint.y) || !Number.isFinite(focalPoint.z)) {
        return null;
    }

    const smartAngles = computeSmartOrbitAngles(splat, sxx, sxz, szz, wSum);

    const lift = config?.liftTowardBoundCenter;
    if (typeof lift === 'number' && lift > 0) {
        const boundCenter = splat.worldBound.center;
        const t = Math.min(1, Math.max(0, lift));
        focalPoint.y = focalPoint.y + (boundCenter.y - focalPoint.y) * t;
    }

    const zoomOut = config?.zoomOutFactor;
    if (typeof zoomOut === 'number' && zoomOut > 1 && Number.isFinite(zoomOut)) {
        radius *= zoomOut;
    }

    // Harden against "from below" framing: ensure the orbit eye stays above the world-space ground bound.
    const worldBound = splat.worldBound;
    const minBoundY = worldBound.center.y - worldBound.halfExtents.y;
    const groundClearance = Math.max(worldBound.halfExtents.y * 0.06, 0.02);
    const minCameraY = minBoundY + groundClearance;
    const requiredSin = radius > 1e-6 ? (minCameraY - focalPoint.y) / radius : 0;
    const requiredElevDeg = Math.asin(clamp(requiredSin, -1, 1)) * (180 / Math.PI);
    const minSafeElevDeg = Math.max(DEFAULT_MIN_ABOVE_GROUND_ELEV_DEG, requiredElevDeg + 1);
    const safeElevDeg = Math.max(smartAngles.elevDeg ?? DEFAULT_SMART_ELEV_DEG, minSafeElevDeg);

    return {
        focalPoint,
        radius,
        ...(smartAngles.azimDeg !== undefined ? { azimDeg: smartAngles.azimDeg } : {}),
        elevDeg: clamp(safeElevDeg, 5, 80),
    };
};

export { computeDensityFocus };
